import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Tabs,
  Tab,
  alpha,
  InputAdornment,
  useTheme,
  Breadcrumbs,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ContentCopy,
  Download,
  Save,
  AutoAwesome,
  Refresh,
  Business,
  Campaign,
  Lightbulb,
  Person,
  Close,
  Delete,
  Visibility,
  Search,
  FilterList,
  Star,
  ChevronRight,
  StarBorder,
  ChatBubbleOutline,
  TrendingUp,
  Group,
  RocketLaunch,
  Psychology,
  Palette,
  Insights,
  AttachMoney,
  LocalOffer,
  Fullscreen,
  FullscreenExit,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link,
  Image,
  Home,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import newBlogService from "../services/new_blogsServices";

const tools = [
  {
    id: "slogan",
    name: "Slogan Generator",
    icon: <LocalOffer />,
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    questions: [
      "Tell us about your brand or business",
      "What are your business's unique selling products/features?",
      "Who is your target audience or user base?",
    ],
    placeholder: "Describe your business details...",
    description: "Create catchy and memorable slogans for your brand",
  },
  {
    id: "strategy",
    name: "Brand Strategy",
    icon: <Insights />,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    questions: [
      "Tell us about your brand or business",
      "What is your brand's or company's mission?",
    ],
    placeholder: "Describe your brand vision...",
    description: "Develop comprehensive brand strategies and positioning",
  },
  {
    id: "idea",
    name: "Business Idea Generator",
    icon: <RocketLaunch />,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    questions: [
      "What industries or areas are you interested in?",
      "What is your available budget range?",
    ],
    placeholder: "Share your interests and budget...",
    description: "Generate innovative business ideas based on your interests",
  },
  {
    id: "persona",
    name: "Persona Generator",
    icon: <Psychology />,
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
    questions: ["What is your company name?", "Tell us about your company"],
    placeholder: "Describe your company...",
    description: "Create detailed customer personas for your business",
  },
];

const AIBusinessTools = () => {
  const theme = useTheme();
  const [activeTool, setActiveTool] = useState(tools[0]);
  const [answers, setAnswers] = useState(
    Array(tools[0].questions.length).fill(""),
  );
  const [generatedContent, setGeneratedContent] = useState("");
  const extractContent = (res) => {
    return res?.data?.content || "";
  };

  const [savedContents, setSavedContents] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [starredItems, setStarredItems] = useState(new Set());
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);

  // Initialize answers based on active tool
  useEffect(() => {
    setAnswers(Array(activeTool.questions.length).fill(""));
  }, [activeTool]);

  const handleToolClick = (tool) => {
    setActiveTool(tool);
    setGeneratedContent("");
    setShowEditorToolbar(false);
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const generateSloganFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      setSnackbar({
        open: true,
        message: "Generating slogans with AI...",
        severity: "info",
      });

      const payload = {
        brand_business: answers[0],
        usp_features: answers[1],
        target_audience: answers[2],
        tone: "friendly",
        language: "English",
      };

      console.log("🚀 SLOGAN PAYLOAD:", payload);

      const res = await newBlogService.generateSlogan(payload);

      console.log("✅ SLOGAN API RESPONSE:", res);

      if (res.status === 200) {
        setGeneratedContent(extractContent(res));
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Slogans generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate slogans",
        severity: "error",
      });
    }
  };

  const generateBrandStrategyFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      setSnackbar({
        open: true,
        message: "Generating brand strategy with AI...",
        severity: "info",
      });

      const payload = {
        brand_business: answers[0],
        mission: answers[1],
        tone: "Professional",
        language: "English",
      };

      console.log("🚀 BRAND STRATEGY PAYLOAD:", payload);

      const res = await newBlogService.generateBrandStrategy(payload);

      console.log("✅ BRAND STRATEGY API RESPONSE:", res);

      if (res.status === 200) {
        setGeneratedContent(extractContent(res));
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Brand strategy generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate brand strategy",
        severity: "error",
      });
    }
  };

  const generateBusinessIdeaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      setSnackbar({
        open: true,
        message: "Generating business ideas with AI...",
        severity: "info",
      });

      const payload = {
        industry_interest: answers[0],
        budget_range: answers[1],
        tone: "friendly",
        language: "English",
      };

      console.log("🚀 BUSINESS IDEA PAYLOAD:", payload);

      const res = await newBlogService.generateBusinessIdea(payload);

      console.log("✅ BUSINESS IDEA RESPONSE:", res);

      if (res.status === 200) {
        setGeneratedContent(extractContent(res));
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Business ideas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate business ideas",
        severity: "error",
      });
    }
  };

  const generatePersonaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      setSnackbar({
        open: true,
        message: "Generating personas with AI...",
        severity: "info",
      });

      const payload = {
        company_name: answers[0],
        company_details: answers[1],
        tone: "professional",
        language: "English",
      };

      console.log("🚀 PERSONA PAYLOAD:", payload);

      const res = await newBlogService.generatePersona(payload);

      console.log("✅ PERSONA RESPONSE:", res);

      if (res.status === 200) {
        setGeneratedContent(extractContent(res));
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Personas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate personas",
        severity: "error",
      });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setSnackbar({
      open: true,
      message: "📋 Copied to clipboard!",
      severity: "success",
    });
  };

  const handleSave = () => {
    if (!generatedContent) {
      setSnackbar({
        open: true,
        message: "Generate content first!",
        severity: "warning",
      });
      return;
    }

    const newSavedContent = {
      id: Date.now(),
      tool: activeTool.name,
      toolId: activeTool.id,
      content: generatedContent,
      date: new Date().toISOString(),
      color: activeTool.color,
      icon: activeTool.icon,
      starred: false,
    };

    setSavedContents([newSavedContent, ...savedContents]);
    setSnackbar({
      open: true,
      message: "💾 Content saved successfully!",
      severity: "success",
    });
  };

  const handleDownload = () => {
    if (!generatedContent) return;

    const element = document.createElement("a");
    const file = new Blob([generatedContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${activeTool.name.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    setSnackbar({
      open: true,
      message: "📥 Download started!",
      severity: "success",
    });
  };

  const handleClear = () => {
    setAnswers(Array(activeTool.questions.length).fill(""));
    setGeneratedContent("");
    setShowEditorToolbar(false);
  };

  const handleStarToggle = (id) => {
    const newStarred = new Set(starredItems);
    if (newStarred.has(id)) {
      newStarred.delete(id);
    } else {
      newStarred.add(id);
    }
    setStarredItems(newStarred);
  };

  const handleDeleteSaved = (id) => {
    setSavedContents(savedContents.filter((item) => item.id !== id));
    setSnackbar({
      open: true,
      message: "🗑️ Item deleted!",
      severity: "info",
    });
  };

  const formatText = (format) => {
    const textarea = document.querySelector("textarea");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = generatedContent.substring(start, end);
    let formattedText = "";

    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "bullet":
        formattedText = `\n• ${selectedText}`;
        break;
      case "number":
        formattedText = `\n1. ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent =
      generatedContent.substring(0, start) +
      formattedText +
      generatedContent.substring(end);
    setGeneratedContent(newContent);
  };

  const filteredSavedContents = savedContents.filter(
    (item) =>
      item.tool.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const starredContents = filteredSavedContents.filter((item) =>
    starredItems.has(item.id),
  );
  const allContents = filteredSavedContents;

  const displayedContents = selectedTab === 0 ? allContents : starredContents;

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        minHeight: "100vh",
        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#0f172a",
      }}
    >
      {/* Breadcrumb */}
      {/* Breadcrumb */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          separator={
            <ChevronRight
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          }
          aria-label="breadcrumb"
        >
          <Link
            component={RouterLink}
            to="/blogpage"
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "0.875rem",
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                color: theme.palette.primary.dark,
              },
            }}
          >
            <Home sx={{ mr: 0.5, fontSize: 16 }} />
            Blog Page
          </Link>

          <Link
            component={RouterLink}
            to="/business-tools"
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "0.875rem",
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                color: theme.palette.primary.dark,
              },
            }}
          >
            <Business sx={{ mr: 0.5, fontSize: 16 }} />
            Business Tools
          </Link>

          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            <AutoAwesome sx={{ mr: 0.5, fontSize: 16 }} />
            AI Business Tool
          </Typography>
        </Breadcrumbs>
      </Box>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          <AutoAwesome sx={{ fontSize: 40, verticalAlign: "middle", mr: 2 }} />
          AI Business Tools
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          Generate professional business content with cutting-edge AI technology
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Panel - Tools & Questions */}
        <Grid xs={12} lg={8}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
            }}
          >
            {/* Tool Selection */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {tools.map((tool) => (
                <Grid xs={6} sm={3} key={tool.id}>
                  <Card
                    onClick={() => handleToolClick(tool)}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border:
                        activeTool.id === tool.id
                          ? `2px solid ${tool.color}`
                          : "1px solid",
                      borderColor:
                        activeTool.id === tool.id ? tool.color : "divider",
                      backgroundColor:
                        activeTool.id === tool.id
                          ? alpha(tool.color, 0.05)
                          : "background.paper",
                      borderRadius: 2,
                      height: "100%",
                      minHeight: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                        borderColor: tool.color,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        textAlign: "center",
                        p: 2,
                        "&:last-child": { pb: 2 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          p: 1.5,
                          borderRadius: "50%",
                          background: tool.gradient,
                          color: "white",
                          mb: 1.5,
                          transition: "transform 0.3s ease",
                          transform:
                            activeTool.id === tool.id
                              ? "scale(1.1)"
                              : "scale(1)",
                        }}
                      >
                        {tool.icon}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                      >
                        {tool.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          fontSize: "0.7rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {tool.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Active Tool Content */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(activeTool.color, 0.03),
                border: `1px solid ${alpha(activeTool.color, 0.1)}`,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 1,
                    borderRadius: 1,
                    background: activeTool.gradient,
                    color: "white",
                    mr: 2,
                  }}
                >
                  {activeTool.icon}
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={activeTool.color}
                  >
                    {activeTool.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Answer these questions to generate content
                  </Typography>
                </Box>
              </Box>

              {activeTool.questions.map((question, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: activeTool.gradient,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {question}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={activeTool.placeholder}
                    value={answers[index]}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: activeTool.color,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ChatBubbleOutline
                            sx={{
                              fontSize: 16,
                              color: alpha(activeTool.color, 0.5),
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ))}

              <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={() => {
                    if (activeTool.id === "slogan") {
                      generateSloganFromAPI();
                    } else if (activeTool.id === "strategy") {
                      generateBrandStrategyFromAPI();
                    } else if (activeTool.id === "idea") {
                      generateBusinessIdeaFromAPI();
                    } else if (activeTool.id === "persona") {
                      generatePersonaFromAPI();
                    }
                  }}
                  size="large"
                  sx={{
                    flex: 1,
                    background: activeTool.gradient,
                    "&:hover": {
                      background: activeTool.gradient,
                      boxShadow: `0 4px 20px ${alpha(activeTool.color, 0.4)}`,
                    },
                    py: 1.5,
                    borderRadius: 1,
                  }}
                >
                  Generate Content
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleClear}
                  size="large"
                  sx={{
                    borderRadius: 1,
                    borderColor: alpha(activeTool.color, 0.3),
                    color: activeTool.color,
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Generated Content & Actions */}
        <Grid xs={12} lg={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                color={activeTool.color}
              >
                ✨ Generated Content
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Fullscreen View">
                  <IconButton
                    size="small"
                    onClick={() => setFullscreenDialogOpen(true)}
                    disabled={!generatedContent}
                    sx={{
                      color: generatedContent
                        ? activeTool.color
                        : "action.disabled",
                      backgroundColor: generatedContent
                        ? alpha(activeTool.color, 0.1)
                        : "transparent",
                      border: `1px solid ${generatedContent ? alpha(activeTool.color, 0.3) : "transparent"}`,
                      "&:hover": {
                        backgroundColor: generatedContent
                          ? alpha(activeTool.color, 0.2)
                          : "transparent",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    <Fullscreen />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Editor Toolbar */}
            {showEditorToolbar && (
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  mb: 2,
                  backgroundColor: alpha(activeTool.color, 0.05),
                  border: `1px solid ${alpha(activeTool.color, 0.1)}`,
                  borderRadius: 1,
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Tooltip title="Bold">
                  <IconButton size="small" onClick={() => formatText("bold")}>
                    <FormatBold fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton size="small" onClick={() => formatText("italic")}>
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bullet List">
                  <IconButton size="small" onClick={() => formatText("bullet")}>
                    <FormatListBulleted fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List">
                  <IconButton size="small" onClick={() => formatText("number")}>
                    <FormatListNumbered fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="Insert Link">
                  <IconButton size="small">
                    <Link fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert Image">
                  <IconButton size="small">
                    <Image fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            )}

            {/* Content Area */}
            {/* Content Area in Right Panel */}
            <Box
              sx={{
                flexGrow: 1,
                mb: 3,
                position: "relative",
                minHeight: 300,
                overflow: "hidden",
              }}
            >
              <TextField
                fullWidth
                multiline
                variant="outlined"
                value={generatedContent}
                onChange={(e) => setGeneratedContent(e.target.value)}
                placeholder="Your generated content will appear here..."
                sx={{
                  height: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: "100%",
                    alignItems: "flex-start",
                    borderRadius: 1,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.875rem",
                    "& .MuiInputBase-inputMultiline": {
                      height: "100% !important",
                      overflow: "auto !important",
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    height: "100%",
                    "& textarea": {
                      height: "100% !important",
                      minHeight: "100% !important",
                      overflow: "auto !important",
                      resize: "none",
                    },
                  },
                }}
              />
            </Box>  

            {/* Action Icons Row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                px: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    onClick={handleCopy}
                    disabled={!generatedContent}
                    sx={{
                      color: generatedContent ? "#6366f1" : "action.disabled",
                      backgroundColor: generatedContent
                        ? alpha("#6366f1", 0.1)
                        : "transparent",
                      "&:hover": {
                        backgroundColor: generatedContent
                          ? alpha("#6366f1", 0.2)
                          : "transparent",
                      },
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save to collection">
                  <IconButton
                    onClick={handleSave}
                    disabled={!generatedContent}
                    sx={{
                      color: generatedContent ? "#10b981" : "action.disabled",
                      backgroundColor: generatedContent
                        ? alpha("#10b981", 0.1)
                        : "transparent",
                      "&:hover": {
                        backgroundColor: generatedContent
                          ? alpha("#10b981", 0.2)
                          : "transparent",
                      },
                    }}
                  >
                    <Save />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Download as text file">
                  <IconButton
                    onClick={handleDownload}
                    disabled={!generatedContent}
                    sx={{
                      color: generatedContent ? "#f59e0b" : "action.disabled",
                      backgroundColor: generatedContent
                        ? alpha("#f59e0b", 0.1)
                        : "transparent",
                      "&:hover": {
                        backgroundColor: generatedContent
                          ? alpha("#f59e0b", 0.2)
                          : "transparent",
                      },
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>

              <Tooltip title="View saved content">
                <IconButton
                  onClick={() => setSaveDialogOpen(true)}
                  sx={{
                    color: "#8b5cf6",
                    backgroundColor: alpha("#8b5cf6", 0.1),
                    "&:hover": {
                      backgroundColor: alpha("#8b5cf6", 0.2),
                    },
                  }}
                >
                  <Badge badgeContent={savedContents.length} color="error">
                    <Visibility />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2}>
              <Grid xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: alpha("#6366f1", 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha("#6366f1", 0.1)}`,
                  }}
                >
                  <Typography variant="h5" color="#6366f1" fontWeight="bold">
                    {savedContents.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Saved Items
                  </Typography>
                </Paper>
              </Grid>
              <Grid xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: alpha("#10b981", 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha("#10b981", 0.1)}`,
                  }}
                >
                  <Typography variant="h5" color="#10b981" fontWeight="bold">
                    {tools.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    AI Tools
                  </Typography>
                </Paper>
              </Grid>
              <Grid xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: alpha("#f59e0b", 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha("#f59e0b", 0.1)}`,
                  }}
                >
                  <Typography variant="h5" color="#f59e0b" fontWeight="bold">
                    {starredItems.size}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Starred
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Fullscreen Content Dialog */}
      {/* Fullscreen Content Dialog */}
      <Dialog
        open={fullscreenDialogOpen}
        onClose={() => setFullscreenDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < 900}
        PaperProps={{
          sx: {
            borderRadius: window.innerWidth >= 900 ? 3 : 0,
            height: window.innerWidth >= 900 ? "90vh" : "100vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
            background: activeTool.gradient,
            color: "white",
            flexShrink: 0, // Prevent title from shrinking
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Fullscreen sx={{ mr: 2 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Fullscreen View - {activeTool.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Edit and view your generated content
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Copy">
              <IconButton
                onClick={handleCopy}
                disabled={!generatedContent}
                sx={{ color: "white" }}
              >
                <ContentCopy />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={() => setFullscreenDialogOpen(false)}
                sx={{ color: "white" }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0, // Important for flexbox scrolling
          }}
        >
          {/* Editor Toolbar in Dialog */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: alpha(activeTool.color, 0.05),
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              flexShrink: 0, // Prevent toolbar from shrinking
            }}
          >
            <Typography variant="caption" fontWeight="medium" sx={{ mr: 1 }}>
              Format:
            </Typography>
            <Tooltip title="Bold">
              <IconButton size="small" onClick={() => formatText("bold")}>
                <FormatBold fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton size="small" onClick={() => formatText("italic")}>
                <FormatItalic fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bullet List">
              <IconButton size="small" onClick={() => formatText("bullet")}>
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
              <IconButton size="small" onClick={() => formatText("number")}>
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Tooltip title="Insert Link">
              <IconButton size="small">
                <Link fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert Image">
              <IconButton size="small">
                <Image fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={!generatedContent}
                color="primary"
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save">
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={!generatedContent}
                color="success"
              >
                <Save />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Fullscreen Content Editor - SCROLLABLE */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
              minHeight: 0,
            }}
          >
            <TextField
              fullWidth
              multiline
              variant="outlined"
              value={generatedContent}
              onChange={(e) => setGeneratedContent(e.target.value)}
              placeholder="Edit your content here..."
              sx={{
                height: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "100%",
                  alignItems: "flex-start",
                  borderRadius: 0,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.95rem",
                  "& .MuiInputBase-inputMultiline": {
                    height: "100% !important",
                    overflow: "auto !important",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                sx: {
                  height: "100%",
                  "& textarea": {
                    height: "100% !important",
                    minHeight: "100% !important",
                    overflow: "auto !important",
                    resize: "none",
                  },
                },
              }}
              inputProps={{
                style: {
                  height: "100%",
                  overflow: "auto",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            justifyContent: "space-between",
            flexShrink: 0, // Prevent actions from shrinking
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Characters: {generatedContent.length} | Words:{" "}
            {generatedContent.split(/\s+/).filter(Boolean).length}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => setFullscreenDialogOpen(false)}
              variant="outlined"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                handleSave();
                setFullscreenDialogOpen(false);
              }}
              variant="contained"
              disabled={!generatedContent}
              sx={{
                background: activeTool.gradient,
                "&:hover": {
                  background: activeTool.gradient,
                },
              }}
            >
              Save & Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Saved Content Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.1),
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Save sx={{ mr: 2, color: "primary.main" }} />
            <Typography variant="h6" fontWeight="bold">
              Saved Content ({savedContents.length})
            </Typography>
          </Box>
          <IconButton onClick={() => setSaveDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {/* Search and Filter */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search saved content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              <Button startIcon={<FilterList />} variant="outlined">
                Filter
              </Button>
            </Box>

            {/* Tabs */}
            <Tabs
              value={selectedTab}
              onChange={(e, v) => setSelectedTab(v)}
              sx={{ mb: 2 }}
            >
              <Tab label={`All Content (${allContents.length})`} />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Star sx={{ fontSize: 16, mr: 0.5 }} />
                    Starred ({starredItems.size})
                  </Box>
                }
              />
            </Tabs>

            {/* Content List */}
            {displayedContents.length > 0 ? (
              <List
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {displayedContents.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      py: 2,
                      "&:hover": {
                        backgroundColor: alpha(item.color, 0.05),
                      },
                    }}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleStarToggle(item.id)}
                          size="small"
                          sx={{
                            color: starredItems.has(item.id)
                              ? "#f59e0b"
                              : "action.active",
                          }}
                        >
                          {starredItems.has(item.id) ? (
                            <Star />
                          ) : (
                            <StarBorder />
                          )}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSaved(item.id)}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          background:
                            item.gradient ||
                            `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
                          color: "white",
                          width: 32,
                          height: 32,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.tool}
                          </Typography>
                          <Chip
                            label={new Date(item.date).toLocaleDateString()}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.content.substring(0, 120)}...
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  height: 200,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                }}
              >
                <Save sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>
                  No saved content yet
                </Typography>
                <Typography variant="body2">
                  Generate and save content to see it here
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setSaveDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              const exportData = savedContents
                .map(
                  (item) =>
                    `=== ${item.tool} ===\nDate: ${new Date(item.date).toLocaleString()}\n\n${item.content}\n\n`,
                )
                .join("\n");

              const element = document.createElement("a");
              const file = new Blob([exportData], { type: "text/plain" });
              element.href = URL.createObjectURL(file);
              element.download = `ai_business_tools_export_${Date.now()}.txt`;
              document.body.appendChild(element);
              element.click();
              document.body.removeChild(element);
            }}
            variant="contained"
            disabled={savedContents.length === 0}
          >
            Export All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AIBusinessTools;



// import React, { useState, useEffect } from "react";
// import {
//   Container,
//   Paper,
//   Typography,
//   TextField,
//   Button,
//   Box,
//   Card,
//   CardContent,
//   Alert,
//   Snackbar,
//   IconButton,
//   Tooltip,
//   Divider,
//   Chip,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   Avatar,
//   Badge,
//   Tabs,
//   Tab,
//   alpha,
//   InputAdornment,
//   useTheme,
//   Breadcrumbs,
//   CircularProgress,
//   Backdrop,
//   Fade,
// } from "@mui/material";
// import Grid from "@mui/material/Unstable_Grid2";
// import {
//   ContentCopy,
//   Download,
//   Save,
//   AutoAwesome,
//   Refresh,
//   Business,
//   Campaign,
//   Lightbulb,
//   Person,
//   Close,
//   Delete,
//   Visibility,
//   Search,
//   FilterList,
//   Star,
//   ChevronRight,
//   StarBorder,
//   ChatBubbleOutline,
//   TrendingUp,
//   Group,
//   RocketLaunch,
//   Psychology,
//   Palette,
//   Insights,
//   AttachMoney,
//   LocalOffer,
//   Fullscreen,
//   FullscreenExit,
//   FormatBold,
//   FormatItalic,
//   FormatListBulleted,
//   FormatListNumbered,
//   Link,
//   Image,
//   Home,
//   Article,
// } from "@mui/icons-material";
// import { Link as RouterLink, useHistory } from "react-router-dom";
// import newBlogService from "../services/new_blogsServices";

// const tools = [
//   {
//     id: "slogan",
//     name: "Slogan Generator",
//     icon: <LocalOffer />,
//     color: "#6366f1",
//     gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//     questions: [
//       "Tell us about your brand or business",
//       "What are your business's unique selling products/features?",
//       "Who is your target audience or user base?",
//     ],
//     placeholder: "Describe your business details...",
//     description: "Create catchy and memorable slogans for your brand",
//   },
//   {
//     id: "strategy",
//     name: "Brand Strategy",
//     icon: <Insights />,
//     color: "#10b981",
//     gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
//     questions: [
//       "Tell us about your brand or business",
//       "What is your brand's or company's mission?",
//     ],
//     placeholder: "Describe your brand vision...",
//     description: "Develop comprehensive brand strategies and positioning",
//   },
//   {
//     id: "idea",
//     name: "Business Idea Generator",
//     icon: <RocketLaunch />,
//     color: "#f59e0b",
//     gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
//     questions: [
//       "What industries or areas are you interested in?",
//       "What is your available budget range?",
//     ],
//     placeholder: "Share your interests and budget...",
//     description: "Generate innovative business ideas based on your interests",
//   },
//   {
//     id: "persona",
//     name: "Persona Generator",
//     icon: <Psychology />,
//     color: "#8b5cf6",
//     gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
//     questions: ["What is your company name?", "Tell us about your company"],
//     placeholder: "Describe your company...",
//     description: "Create detailed customer personas for your business",
//   },
// ];

// const AIBusinessTools = () => {
//   const theme = useTheme();
//   const history = useHistory();
//   const [activeTool, setActiveTool] = useState(tools[0]);
//   const [answers, setAnswers] = useState(
//     Array(tools[0].questions.length).fill(""),
//   );
//   const [generatedContent, setGeneratedContent] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [loadingTool, setLoadingTool] = useState(null);

//   const extractContent = (res) => {
//     return res?.data?.content || "";
//   };

//   const [savedContents, setSavedContents] = useState([]);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [saveDialogOpen, setSaveDialogOpen] = useState(false);
//   const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedTab, setSelectedTab] = useState(0);
//   const [starredItems, setStarredItems] = useState(new Set());
//   const [showEditorToolbar, setShowEditorToolbar] = useState(false);

//   // Initialize answers based on active tool
//   useEffect(() => {
//     setAnswers(Array(activeTool.questions.length).fill(""));
//   }, [activeTool]);

//   const handleToolClick = (tool) => {
//     setActiveTool(tool);
//     setGeneratedContent("");
//     setShowEditorToolbar(false);
//   };

//   const handleAnswerChange = (index, value) => {
//     const newAnswers = [...answers];
//     newAnswers[index] = value;
//     setAnswers(newAnswers);
//   };

//   const startLoading = (toolId) => {
//     setLoading(true);
//     setLoadingTool(toolId);
//   };

//   const stopLoading = () => {
//     setLoading(false);
//     setLoadingTool(null);
//   };

//   const generateSloganFromAPI = async () => {
//     if (answers.some((a) => a.trim() === "")) {
//       setSnackbar({
//         open: true,
//         message: "Please answer all questions before generating!",
//         severity: "warning",
//       });
//       return;
//     }

//     try {
//       startLoading("slogan");

//       const payload = {
//         brand_business: answers[0],
//         usp_features: answers[1],
//         target_audience: answers[2],
//         tone: "friendly",
//         language: "English",
//       };

//       const res = await newBlogService.generateSlogan(payload);

//       if (res.status === 200) {
//         setGeneratedContent(extractContent(res));
//         setShowEditorToolbar(true);

//         setSnackbar({
//           open: true,
//           message: "✨ Slogans generated successfully!",
//           severity: "success",
//         });
//       }
//     } catch (err) {
//       console.error(err);
//       setSnackbar({
//         open: true,
//         message: "Failed to generate slogans",
//         severity: "error",
//       });
//     } finally {
//       stopLoading();
//     }
//   };

//   const generateBrandStrategyFromAPI = async () => {
//     if (answers.some((a) => a.trim() === "")) {
//       setSnackbar({
//         open: true,
//         message: "Please answer all questions before generating!",
//         severity: "warning",
//       });
//       return;
//     }

//     try {
//       startLoading("strategy");

//       const payload = {
//         brand_business: answers[0],
//         mission: answers[1],
//         tone: "Professional",
//         language: "English",
//       };

//       const res = await newBlogService.generateBrandStrategy(payload);

//       if (res.status === 200) {
//         setGeneratedContent(extractContent(res));
//         setShowEditorToolbar(true);

//         setSnackbar({
//           open: true,
//           message: "✨ Brand strategy generated successfully!",
//           severity: "success",
//         });
//       }
//     } catch (err) {
//       console.error(err);
//       setSnackbar({
//         open: true,
//         message: "Failed to generate brand strategy",
//         severity: "error",
//       });
//     } finally {
//       stopLoading();
//     }
//   };

//   const generateBusinessIdeaFromAPI = async () => {
//     if (answers.some((a) => a.trim() === "")) {
//       setSnackbar({
//         open: true,
//         message: "Please answer all questions before generating!",
//         severity: "warning",
//       });
//       return;
//     }

//     try {
//       startLoading("idea");

//       const payload = {
//         industry_interest: answers[0],
//         budget_range: answers[1],
//         tone: "friendly",
//         language: "English",
//       };

//       const res = await newBlogService.generateBusinessIdea(payload);

//       if (res.status === 200) {
//         setGeneratedContent(extractContent(res));
//         setShowEditorToolbar(true);

//         setSnackbar({
//           open: true,
//           message: "✨ Business ideas generated successfully!",
//           severity: "success",
//         });
//       }
//     } catch (err) {
//       console.error(err);
//       setSnackbar({
//         open: true,
//         message: "Failed to generate business ideas",
//         severity: "error",
//       });
//     } finally {
//       stopLoading();
//     }
//   };

//   const generatePersonaFromAPI = async () => {
//     if (answers.some((a) => a.trim() === "")) {
//       setSnackbar({
//         open: true,
//         message: "Please answer all questions before generating!",
//         severity: "warning",
//       });
//       return;
//     }

//     try {
//       startLoading("persona");

//       const payload = {
//         company_name: answers[0],
//         company_details: answers[1],
//         tone: "professional",
//         language: "English",
//       };

//       const res = await newBlogService.generatePersona(payload);

//       if (res.status === 200) {
//         setGeneratedContent(extractContent(res));
//         setShowEditorToolbar(true);

//         setSnackbar({
//           open: true,
//           message: "✨ Personas generated successfully!",
//           severity: "success",
//         });
//       }
//     } catch (err) {
//       console.error(err);
//       setSnackbar({
//         open: true,
//         message: "Failed to generate personas",
//         severity: "error",
//       });
//     } finally {
//       stopLoading();
//     }
//   };

//   const handleCopy = () => {
//     navigator.clipboard.writeText(generatedContent);
//     setSnackbar({
//       open: true,
//       message: "📋 Copied to clipboard!",
//       severity: "success",
//     });
//   };

//   const handleSave = () => {
//     if (!generatedContent) {
//       setSnackbar({
//         open: true,
//         message: "Generate content first!",
//         severity: "warning",
//       });
//       return;
//     }

//     const newSavedContent = {
//       id: Date.now(),
//       tool: activeTool.name,
//       toolId: activeTool.id,
//       content: generatedContent.substring(0, 100) + "...", // Preview only
//       fullContent: generatedContent,
//       date: new Date().toISOString(),
//       color: activeTool.color,
//       icon: activeTool.icon,
//       starred: false,
//     };

//     setSavedContents([newSavedContent, ...savedContents]);
//     setSaveDialogOpen(true);
//     setSnackbar({
//       open: true,
//       message: "💾 Content saved successfully!",
//       severity: "success",
//     });
//   };

//   const handleDownload = () => {
//     if (!generatedContent) return;

//     const element = document.createElement("a");
//     const file = new Blob([generatedContent], { type: "text/plain" });
//     element.href = URL.createObjectURL(file);
//     element.download = `${activeTool.name.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.txt`;
//     document.body.appendChild(element);
//     element.click();
//     document.body.removeChild(element);

//     setSnackbar({
//       open: true,
//       message: "📥 Download started!",
//       severity: "success",
//     });
//   };

//   const handleClear = () => {
//     setAnswers(Array(activeTool.questions.length).fill(""));
//     setGeneratedContent("");
//     setShowEditorToolbar(false);
//   };

//   const handleStarToggle = (id) => {
//     const newStarred = new Set(starredItems);
//     if (newStarred.has(id)) {
//       newStarred.delete(id);
//     } else {
//       newStarred.add(id);
//     }
//     setStarredItems(newStarred);
//   };

//   const handleDeleteSaved = (id) => {
//     setSavedContents(savedContents.filter((item) => item.id !== id));
//     setSnackbar({
//       open: true,
//       message: "🗑️ Item deleted!",
//       severity: "info",
//     });
//   };

//   const formatText = (format) => {
//     const textarea = document.querySelector("textarea");
//     if (!textarea) return;

//     const start = textarea.selectionStart;
//     const end = textarea.selectionEnd;
//     const selectedText = generatedContent.substring(start, end);
//     let formattedText = "";

//     switch (format) {
//       case "bold":
//         formattedText = `**${selectedText}**`;
//         break;
//       case "italic":
//         formattedText = `*${selectedText}*`;
//         break;
//       case "bullet":
//         formattedText = `\n• ${selectedText}`;
//         break;
//       case "number":
//         formattedText = `\n1. ${selectedText}`;
//         break;
//       default:
//         formattedText = selectedText;
//     }

//     const newContent =
//       generatedContent.substring(0, start) +
//       formattedText +
//       generatedContent.substring(end);
//     setGeneratedContent(newContent);
//   };

//   const filteredSavedContents = savedContents.filter(
//     (item) =>
//       item.tool.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       item.content.toLowerCase().includes(searchTerm.toLowerCase()),
//   );

//   const starredContents = filteredSavedContents.filter((item) =>
//     starredItems.has(item.id),
//   );
//   const allContents = filteredSavedContents;

//   const displayedContents = selectedTab === 0 ? allContents : starredContents;

//   // Modern Fullscreen Loader Component
//   const FullscreenLoader = ({ tool }) => (
//     <Fade in={loading && loadingTool === tool.id} timeout={300}>
//       <Backdrop
//         open={loading && loadingTool === tool.id}
//         sx={{
//           zIndex: 9999,
//           color: "#fff",
//           background: tool.gradient,
//           display: "flex",
//           flexDirection: "column",
//           gap: 3,
//           backdropFilter: "blur(10px)",
//           backgroundColor: alpha(tool.color, 0.95),
//         }}
//       >
//         {/* Floating particles */}
//         <Box
//           sx={{
//             position: "absolute",
//             width: "100%",
//             height: "100%",
//             overflow: "hidden",
//           }}
//         >
//           {[...Array(8)].map((_, i) => (
//             <Box
//               key={i}
//               sx={{
//                 position: "absolute",
//                 width: Math.random() * 60 + 20,
//                 height: Math.random() * 60 + 20,
//                 background: alpha("#fff", Math.random() * 0.2 + 0.05),
//                 borderRadius: "50%",
//                 left: `${Math.random() * 100}%`,
//                 top: `${Math.random() * 100}%`,
//                 animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite`,
//                 animationDelay: `${Math.random() * 1}s`,
//                 filter: "blur(2px)",
//                 "@keyframes float": {
//                   "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
//                   "50%": { transform: "translateY(-20px) rotate(180deg)" },
//                 },
//               }}
//             />
//           ))}
//         </Box>

//         {/* Main Loader Content */}
//         <Box
//           sx={{
//             textAlign: "center",
//             zIndex: 1,
//             maxWidth: 500,
//             mx: "auto",
//             px: 2,
//           }}
//         >
//           {/* Animated Icon Container */}
//           <Box
//             sx={{
//               width: 100,
//               height: 100,
//               borderRadius: "50%",
//               background: `radial-gradient(circle at 30% 30%, ${alpha("#fff", 0.2)}, transparent)`,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               margin: "0 auto 32px",
//               position: "relative",
//               boxShadow: `
//                 0 0 60px ${alpha(tool.color, 0.4)},
//                 inset 0 0 20px ${alpha("#fff", 0.1)}
//               `,
//             }}
//           >
//             {/* Pulsing Ring */}
//             <Box
//               sx={{
//                 position: "absolute",
//                 width: "120%",
//                 height: "120%",
//                 borderRadius: "50%",
//                 border: `2px solid ${alpha("#fff", 0.2)}`,
//                 animation: "pulseRing 2s ease-out infinite",
//                 "@keyframes pulseRing": {
//                   "0%": {
//                     transform: "scale(0.8)",
//                     opacity: 0.8,
//                   },
//                   "100%": {
//                     transform: "scale(1.2)",
//                     opacity: 0,
//                   },
//                 },
//               }}
//             />

//             {/* Rotating Icon */}
//             <Box
//               sx={{
//                 animation: "rotate 3s linear infinite",
//                 "@keyframes rotate": {
//                   "0%": { transform: "rotate(0deg)" },
//                   "100%": { transform: "rotate(360deg)" },
//                 },
//               }}
//             >
//               <Box
//                 sx={{
//                   animation: "counterRotate 3s linear infinite",
//                   "@keyframes counterRotate": {
//                     "0%": { transform: "rotate(0deg)" },
//                     "100%": { transform: "rotate(-360deg)" },
//                   },
//                 }}
//               >
//                 {React.cloneElement(tool.icon, {
//                   sx: { fontSize: 48, color: "#fff" },
//                 })}
//               </Box>
//             </Box>
//           </Box>

//           {/* Loading Text */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h3"
//               fontWeight="bold"
//               sx={{
//                 mb: 2,
//                 textShadow: "0 2px 10px rgba(0,0,0,0.2)",
//                 fontSize: { xs: "2rem", sm: "2.5rem" },
//               }}
//             >
//               Crafting Your {tool.name}
//             </Typography>
//             <Typography
//               variant="h6"
//               sx={{
//                 opacity: 0.9,
//                 fontWeight: 400,
//                 mb: 3,
//                 maxWidth: 400,
//                 mx: "auto",
//               }}
//             >
//               Our AI is generating unique content tailored for your needs
//             </Typography>
//           </Box>

//           {/* Progress Bar */}
//           <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", mb: 4 }}>
//             <Box
//               sx={{
//                 width: "100%",
//                 height: 4,
//                 backgroundColor: alpha("#fff", 0.2),
//                 borderRadius: 2,
//                 overflow: "hidden",
//               }}
//             >
//               <Box
//                 sx={{
//                   width: "75%",
//                   height: "100%",
//                   background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.5))",
//                   borderRadius: 2,
//                   animation: "progressShimmer 2s ease-in-out infinite",
//                   "@keyframes progressShimmer": {
//                     "0%": { transform: "translateX(-100%)" },
//                     "100%": { transform: "translateX(400%)" },
//                   },
//                 }}
//               />
//             </Box>
//             <Typography
//               variant="caption"
//               sx={{
//                 display: "block",
//                 textAlign: "center",
//                 mt: 1,
//                 opacity: 0.7,
//                 fontSize: "0.875rem",
//               }}
//             >
//               This usually takes 10-30 seconds
//             </Typography>
//           </Box>

//           {/* Loading Dots */}
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "center",
//               gap: 2,
//               mt: 4,
//             }}
//           >
//             {[...Array(3)].map((_, i) => (
//               <Box
//                 key={i}
//                 sx={{
//                   width: 12,
//                   height: 12,
//                   borderRadius: "50%",
//                   backgroundColor: "#fff",
//                   animation: `bounce 1.4s infinite ${i * 0.2}s`,
//                   "@keyframes bounce": {
//                     "0%, 80%, 100%": {
//                       transform: "scale(0)",
//                       opacity: 0.5,
//                     },
//                     "40%": {
//                       transform: "scale(1)",
//                       opacity: 1,
//                     },
//                   },
//                 }}
//               />
//             ))}
//           </Box>

//           {/* Subtle Tips */}
//           <Box
//             sx={{
//               position: "absolute",
//               bottom: 60,
//               left: 0,
//               right: 0,
//               textAlign: "center",
//               zIndex: 1,
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 opacity: 0.6,
//                 fontSize: "0.75rem",
//                 letterSpacing: "0.5px",
//                 textTransform: "uppercase",
//               }}
//             >
//               Powered by Advanced AI • Secure Processing
//             </Typography>
//           </Box>
//         </Box>
//       </Backdrop>
//     </Fade>
//   );

//   // Modern Card Loader (for generate button)
//   const CardLoader = () => (
//     <Box
//       sx={{
//         position: "absolute",
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         background: alpha("#fff", 0.92),
//         backdropFilter: "blur(4px)",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         zIndex: 100,
//         borderRadius: 3,
//         border: `1px solid ${alpha(activeTool.color, 0.2)}`,
//       }}
//     >
//       <Box sx={{ textAlign: "center", px: 3 }}>
//         {/* Spinning Gradient Circle */}
//         <Box
//           sx={{
//             width: 60,
//             height: 60,
//             borderRadius: "50%",
//             margin: "0 auto 16px",
//             position: "relative",
//             background: `conic-gradient(${activeTool.color} 0deg, ${alpha(activeTool.color, 0.3)} 360deg)`,
//             animation: "spin 1s linear infinite",
//             "@keyframes spin": {
//               "0%": { transform: "rotate(0deg)" },
//               "100%": { transform: "rotate(360deg)" },
//             },
//             "&::before": {
//               content: '""',
//               position: "absolute",
//               inset: 4,
//               background: "#fff",
//               borderRadius: "50%",
//             },
//           }}
//         >
//           <Box
//             sx={{
//               position: "absolute",
//               top: "50%",
//               left: "50%",
//               transform: "translate(-50%, -50%)",
//               zIndex: 1,
//             }}
//           >
//             {React.cloneElement(activeTool.icon, {
//               sx: { fontSize: 24, color: activeTool.color },
//             })}
//           </Box>
//         </Box>

//         <Typography
//           variant="h6"
//           color="text.primary"
//           fontWeight="bold"
//           sx={{ mb: 1 }}
//         >
//           Generating {activeTool.name}
//         </Typography>
//         <Typography
//           variant="body2"
//           color="text.secondary"
//           sx={{
//             maxWidth: 300,
//             mx: "auto",
//             lineHeight: 1.4,
//           }}
//         >
//           Please wait while our AI creates amazing content for you...
//         </Typography>

//         {/* Mini Progress Dots */}
//         <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
//           {[...Array(5)].map((_, i) => (
//             <Box
//               key={i}
//               sx={{
//                 width: 6,
//                 height: 6,
//                 borderRadius: "50%",
//                 backgroundColor: activeTool.color,
//                 opacity: 0.3,
//                 animation: `pulseMini 1s ease-in-out infinite ${i * 0.1}s`,
//                 "@keyframes pulseMini": {
//                   "0%, 100%": { opacity: 0.3, transform: "scale(1)" },
//                   "50%": { opacity: 1, transform: "scale(1.2)" },
//                 },
//               }}
//             />
//           ))}
//         </Box>
//       </Box>
//     </Box>
//   );

//   return (
//     <Container
//       maxWidth="xl"
//       sx={{
//         py: 4,
//         minHeight: "100vh",
//         backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#0f172a",
//         position: "relative",
//       }}
//     >
//       {/* Individual Tool Loaders */}
//       {tools.map((tool) => (
//         <FullscreenLoader key={tool.id} tool={tool} />
//       ))}

//       {/* Modern Breadcrumb */}
//       <Box sx={{ mb: 4 }}>
//         <Paper
//           elevation={0}
//           sx={{
//             p: 2,
//             borderRadius: 2,
//             backgroundColor: alpha(theme.palette.primary.main, 0.03),
//             border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//           }}
//         >
//           <Breadcrumbs
//             separator={
//               <ChevronRight
//                 fontSize="small"
//                 sx={{ color: alpha(theme.palette.text.secondary, 0.5) }}
//               />
//             }
//             aria-label="breadcrumb"
//           >
//             <Tooltip title="Go to Blog Page">
//               <Box
//                 component={RouterLink}
//                 to="/blogpage"
//                 sx={{
//                   display: "flex",
//                   alignItems: "center",
//                   textDecoration: "none",
//                   color: theme.palette.text.secondary,
//                   padding: "4px 12px",
//                   borderRadius: 1,
//                   backgroundColor: alpha(theme.palette.primary.main, 0.05),
//                   transition: "all 0.2s ease",
//                   "&:hover": {
//                     color: theme.palette.primary.main,
//                     backgroundColor: alpha(theme.palette.primary.main, 0.1),
//                     transform: "translateY(-1px)",
//                   },
//                 }}
//               >
//                 <Article sx={{ mr: 1, fontSize: 18 }} />
//                 <Typography variant="body2" fontWeight="medium">
//                   Blog Page
//                 </Typography>
//               </Box>
//             </Tooltip>

//             <Box
//               sx={{
//                 display: "flex",
//                 alignItems: "center",
//                 px: 2,
//                 py: 0.5,
//                 backgroundColor: theme.palette.primary.main,
//                 borderRadius: 1,
//                 color: "white",
//                 boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
//               }}
//             >
//               <AutoAwesome sx={{ mr: 1, fontSize: 18 }} />
//               <Typography variant="body2" fontWeight="bold">
//                 AI Business Tools
//               </Typography>
//             </Box>
//           </Breadcrumbs>

//           {/* Subtitle */}
//           <Typography
//             variant="caption"
//             sx={{
//               display: "block",
//               mt: 1,
//               px: 2,
//               color: theme.palette.text.secondary,
//               fontSize: "0.75rem",
//             }}
//           >
//             Generate professional business content with cutting-edge AI
//             technology
//           </Typography>
//         </Paper>
//       </Box>

//       {/* Centered Header */}
//       <Box
//         sx={{
//           textAlign: "center",
//           mb: 6,
//           position: "relative",
//           maxWidth: 800,
//           mx: "auto",
//         }}
//       >
//         {/* Decorative Background */}
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: "100%",
//             height: "100%",
//             background:
//               "radial-gradient(circle at center, transparent 30%, rgba(99, 102, 241, 0.03) 70%, transparent 100%)",
//             filter: "blur(40px)",
//             zIndex: 0,
//           }}
//         />

//         <Box sx={{ position: "relative", zIndex: 1 }}>
//           {/* Animated Icon */}
//           <Box
//             sx={{
//               display: "inline-flex",
//               p: 2,
//               borderRadius: "50%",
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               mb: 3,
//               animation: "floatIcon 3s ease-in-out infinite",
//               "@keyframes floatIcon": {
//                 "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
//                 "50%": { transform: "translateY(-10px) rotate(10deg)" },
//               },
//             }}
//           >
//             <AutoAwesome sx={{ fontSize: 40 }} />
//           </Box>

//           <Typography
//             variant="h2"
//             component="h1"
//             gutterBottom
//             sx={{
//               fontWeight: "bold",
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f59e0b 100%)",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//               mb: 2,
//               fontSize: { xs: "2.5rem", md: "3.5rem" },
//             }}
//           >
//             AI Business Tools
//           </Typography>
//           <Typography
//             variant="h6"
//             sx={{
//               color: "text.secondary",
//               maxWidth: 600,
//               mx: "auto",
//               lineHeight: 1.6,
//               fontWeight: 400,
//             }}
//           >
//             Generate professional business content, strategies, and ideas with
//             cutting-edge AI technology. Perfect for entrepreneurs, marketers,
//             and business owners.
//           </Typography>
//         </Box>
//       </Box>

//       <Grid container spacing={3}>
//         {/* Left Panel - Tools & Questions */}
//         <Grid xs={12} lg={8}>
//           <Paper
//             elevation={0}
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               backgroundColor: "background.paper",
//               height: "100%",
//               position: "relative",
//               border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
//               boxShadow: `
//                 0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
//                 0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
//               `,
//               overflow: "hidden",
//             }}
//           >
//             {/* Inline Loader for Generate Button */}
//             {loading && loadingTool === activeTool.id && <CardLoader />}

//             {/* Tool Selection */}
//             <Box sx={{ mb: 4 }}>
//               <Typography
//                 variant="h6"
//                 fontWeight="bold"
//                 sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
//               >
//                 <Palette sx={{ color: activeTool.color }} />
//                 Select AI Tool
//               </Typography>
//               <Grid container spacing={2}>
//                 {tools.map((tool) => (
//                   <Grid xs={6} sm={3} key={tool.id}>
//                     <Card
//                       onClick={() => handleToolClick(tool)}
//                       sx={{
//                         cursor: "pointer",
//                         transition: "all 0.3s ease",
//                         border:
//                           activeTool.id === tool.id
//                             ? `2px solid ${tool.color}`
//                             : "1px solid",
//                         borderColor:
//                           activeTool.id === tool.id ? tool.color : "divider",
//                         backgroundColor:
//                           activeTool.id === tool.id
//                             ? alpha(tool.color, 0.05)
//                             : "background.paper",
//                         borderRadius: 2,
//                         height: "100%",
//                         minHeight: 120,
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "center",
//                         position: "relative",
//                         overflow: "hidden",
//                         "&::before": {
//                           content: '""',
//                           position: "absolute",
//                           top: 0,
//                           left: 0,
//                           right: 0,
//                           height: "4px",
//                           background: tool.gradient,
//                           opacity: activeTool.id === tool.id ? 1 : 0,
//                           transition: "opacity 0.3s ease",
//                         },
//                         "&:hover": {
//                           transform: "translateY(-4px)",
//                           boxShadow: `0 12px 24px ${alpha(tool.color, 0.15)}`,
//                           borderColor: tool.color,
//                           "&::before": { opacity: 1 },
//                         },
//                       }}
//                     >
//                       <CardContent
//                         sx={{
//                           textAlign: "center",
//                           p: 2,
//                           "&:last-child": { pb: 2 },
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             display: "inline-flex",
//                             p: 1.5,
//                             borderRadius: "12px",
//                             background: tool.gradient,
//                             color: "white",
//                             mb: 1.5,
//                             transition: "all 0.3s ease",
//                             transform:
//                               activeTool.id === tool.id
//                                 ? "scale(1.1) rotate(5deg)"
//                                 : "scale(1)",
//                           }}
//                         >
//                           {tool.icon}
//                         </Box>
//                         <Typography
//                           variant="subtitle2"
//                           fontWeight="bold"
//                           sx={{ mb: 0.5 }}
//                         >
//                           {tool.name}
//                         </Typography>
//                         <Typography
//                           variant="caption"
//                           color="text.secondary"
//                           sx={{
//                             display: "block",
//                             fontSize: "0.7rem",
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {tool.description}
//                         </Typography>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 ))}
//               </Grid>
//             </Box>

//             {/* Active Tool Content */}
//             <Box
//               sx={{
//                 p: 3,
//                 borderRadius: 2,
//                 backgroundColor: alpha(activeTool.color, 0.03),
//                 border: `1px solid ${alpha(activeTool.color, 0.1)}`,
//                 mb: 3,
//                 position: "relative",
//                 overflow: "hidden",
//                 "&::before": {
//                   content: '""',
//                   position: "absolute",
//                   top: 0,
//                   left: 0,
//                   right: 0,
//                   height: "4px",
//                   background: activeTool.gradient,
//                 },
//               }}
//             >
//               <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
//                 <Box
//                   sx={{
//                     display: "inline-flex",
//                     p: 1.5,
//                     borderRadius: "12px",
//                     background: activeTool.gradient,
//                     color: "white",
//                     mr: 2,
//                     animation: "pulse 2s ease-in-out infinite",
//                     "@keyframes pulse": {
//                       "0%, 100%": { boxShadow: `0 0 0 0 ${alpha(activeTool.color, 0.4)}` },
//                       "50%": { boxShadow: `0 0 0 10px ${alpha(activeTool.color, 0)}` },
//                     },
//                   }}
//                 >
//                   {activeTool.icon}
//                 </Box>
//                 <Box>
//                   <Typography
//                     variant="h5"
//                     fontWeight="bold"
//                     color={activeTool.color}
//                   >
//                     {activeTool.name}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Answer these questions to generate amazing content
//                   </Typography>
//                 </Box>
//               </Box>

//               {activeTool.questions.map((question, index) => (
//                 <Box key={index} sx={{ mb: 3 }}>
//                   <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
//                     <Box
//                       sx={{
//                         width: 28,
//                         height: 28,
//                         borderRadius: "8px",
//                         background: activeTool.gradient,
//                         color: "white",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         mr: 2,
//                         fontSize: "0.875rem",
//                         fontWeight: "bold",
//                         flexShrink: 0,
//                         boxShadow: `0 2px 8px ${alpha(activeTool.color, 0.3)}`,
//                       }}
//                     >
//                       {index + 1}
//                     </Box>
//                     <Typography variant="body1" fontWeight="medium">
//                       {question}
//                     </Typography>
//                   </Box>
//                   <TextField
//                     fullWidth
//                     multiline
//                     rows={3}
//                     variant="outlined"
//                     placeholder={activeTool.placeholder}
//                     value={answers[index]}
//                     onChange={(e) => handleAnswerChange(index, e.target.value)}
//                     sx={{
//                       "& .MuiOutlinedInput-root": {
//                         borderRadius: 1,
//                         transition: "all 0.2s",
//                         "&:hover .MuiOutlinedInput-notchedOutline": {
//                           borderColor: activeTool.color,
//                           borderWidth: 2,
//                         },
//                         "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//                           borderColor: activeTool.color,
//                           borderWidth: 2,
//                         },
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <ChatBubbleOutline
//                             sx={{
//                               fontSize: 18,
//                               color: alpha(activeTool.color, 0.6),
//                             }}
//                           />
//                         </InputAdornment>
//                       ),
//                     }}
//                   />
//                 </Box>
//               ))}

//               <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
//                 <Button
//                   variant="contained"
//                   startIcon={
//                     loading && loadingTool === activeTool.id ? (
//                       <CircularProgress size={20} color="inherit" />
//                     ) : (
//                       <AutoAwesome />
//                     )
//                   }
//                   onClick={() => {
//                     if (activeTool.id === "slogan") {
//                       generateSloganFromAPI();
//                     } else if (activeTool.id === "strategy") {
//                       generateBrandStrategyFromAPI();
//                     } else if (activeTool.id === "idea") {
//                       generateBusinessIdeaFromAPI();
//                     } else if (activeTool.id === "persona") {
//                       generatePersonaFromAPI();
//                     }
//                   }}
//                   size="large"
//                   disabled={loading}
//                   sx={{
//                     flex: 1,
//                     background: activeTool.gradient,
//                     "&:hover": {
//                       background: activeTool.gradient,
//                       boxShadow: `0 8px 30px ${alpha(activeTool.color, 0.4)}`,
//                       transform: "translateY(-2px)",
//                     },
//                     py: 1.5,
//                     borderRadius: 1,
//                     position: "relative",
//                     overflow: "hidden",
//                     fontWeight: "bold",
//                     fontSize: "1rem",
//                     transition: "all 0.3s ease",
//                     "&::before": {
//                       content: '""',
//                       position: "absolute",
//                       top: 0,
//                       left: "-100%",
//                       width: "100%",
//                       height: "100%",
//                       background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
//                       transition: "left 0.7s ease",
//                     },
//                     "&:hover::before": {
//                       left: "100%",
//                     },
//                   }}
//                 >
//                   {loading && loadingTool === activeTool.id
//                     ? "Generating..."
//                     : "Generate Content"}
//                 </Button>

//                 <Button
//                   variant="outlined"
//                   startIcon={<Refresh />}
//                   onClick={handleClear}
//                   size="large"
//                   disabled={loading}
//                   sx={{
//                     borderRadius: 1,
//                     borderColor: alpha(activeTool.color, 0.5),
//                     color: activeTool.color,
//                     "&:hover": {
//                       borderColor: activeTool.color,
//                       backgroundColor: alpha(activeTool.color, 0.05),
//                       transform: "translateY(-1px)",
//                     },
//                     transition: "all 0.2s ease",
//                   }}
//                 >
//                   Clear
//                 </Button>
//               </Box>
//             </Box>
//           </Paper>
//         </Grid>

//         {/* Right Panel - Generated Content & Actions */}
//         <Grid xs={12} lg={4}>
//           <Paper
//             elevation={0}
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               backgroundColor: "background.paper",
//               height: "100%",
//               display: "flex",
//               flexDirection: "column",
//               border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
//               boxShadow: `
//                 0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
//                 0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
//               `,
//             }}
//           >
//             {/* Header with Actions */}
//             <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
//               <Typography variant="h6" fontWeight="bold">
//                 Generated Content
//               </Typography>
//               <Box sx={{ display: "flex", gap: 1 }}>
//                 <Tooltip title="Fullscreen">
//                   <IconButton 
//                     size="small" 
//                     onClick={() => setFullscreenDialogOpen(true)}
//                     disabled={!generatedContent}
//                   >
//                     <Fullscreen fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//               </Box>
//             </Box>

//             {/* Editor Toolbar */}
//             {showEditorToolbar && (
//               <Paper
//                 elevation={0}
//                 sx={{
//                   p: 1,
//                   mb: 2,
//                   borderRadius: 1,
//                   backgroundColor: alpha(theme.palette.primary.main, 0.05),
//                   border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
//                   display: "flex",
//                   gap: 0.5,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Tooltip title="Bold">
//                   <IconButton size="small" onClick={() => formatText("bold")}>
//                     <FormatBold fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//                 <Tooltip title="Italic">
//                   <IconButton size="small" onClick={() => formatText("italic")}>
//                     <FormatItalic fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//                 <Divider orientation="vertical" flexItem />
//                 <Tooltip title="Bullet List">
//                   <IconButton size="small" onClick={() => formatText("bullet")}>
//                     <FormatListBulleted fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//                 <Tooltip title="Numbered List">
//                   <IconButton size="small" onClick={() => formatText("number")}>
//                     <FormatListNumbered fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//                 <Divider orientation="vertical" flexItem />
//                 <Tooltip title="Add Link">
//                   <IconButton size="small">
//                     <Link fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//                 <Tooltip title="Add Image">
//                   <IconButton size="small">
//                     <Image fontSize="small" />
//                   </IconButton>
//                 </Tooltip>
//               </Paper>
//             )}

//             {/* Generated Content Display */}
//             <Box sx={{ flex: 1, mb: 3, position: "relative" }}>
//               {generatedContent ? (
//                 <TextField
//                   fullWidth
//                   multiline
//                   rows={20}
//                   value={generatedContent}
//                   onChange={(e) => setGeneratedContent(e.target.value)}
//                   sx={{
//                     "& .MuiOutlinedInput-root": {
//                       height: "100%",
//                       alignItems: "flex-start",
//                       "& textarea": {
//                         height: "100% !important",
//                         resize: "vertical",
//                       },
//                     },
//                   }}
//                   InputProps={{
//                     sx: {
//                       height: "100%",
//                       backgroundColor: alpha(activeTool.color, 0.03),
//                       border: `1px solid ${alpha(activeTool.color, 0.1)}`,
//                     },
//                   }}
//                 />
//               ) : (
//                 <Box
//                   sx={{
//                     height: "100%",
//                     display: "flex",
//                     flexDirection: "column",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     p: 4,
//                     textAlign: "center",
//                     borderRadius: 2,
//                     backgroundColor: alpha(theme.palette.divider, 0.05),
//                     border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`,
//                   }}
//                 >
//                   <AutoAwesome
//                     sx={{
//                       fontSize: 48,
//                       color: alpha(theme.palette.text.secondary, 0.3),
//                       mb: 2,
//                     }}
//                   />
//                   <Typography variant="h6" color="text.secondary" gutterBottom>
//                     Generated Content
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary">
//                     Your AI-generated content will appear here. Fill in the questions on
//                     the left and click "Generate Content" to get started.
//                   </Typography>
//                 </Box>
//               )}
//             </Box>

//             {/* Action Buttons */}
//             <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
//               <Tooltip title="Copy to clipboard">
//                 <Button
//                   variant="outlined"
//                   startIcon={<ContentCopy />}
//                   onClick={handleCopy}
//                   disabled={!generatedContent}
//                   fullWidth
//                   sx={{
//                     borderRadius: 1,
//                     borderColor: alpha(activeTool.color, 0.3),
//                     color: activeTool.color,
//                     "&:hover": {
//                       borderColor: activeTool.color,
//                       backgroundColor: alpha(activeTool.color, 0.05),
//                     },
//                   }}
//                 >
//                   Copy
//                 </Button>
//               </Tooltip>

//               <Tooltip title="Save for later">
//                 <Button
//                   variant="outlined"
//                   startIcon={<Save />}
//                   onClick={handleSave}
//                   disabled={!generatedContent}
//                   fullWidth
//                   sx={{
//                     borderRadius: 1,
//                     borderColor: alpha(activeTool.color, 0.3),
//                     color: activeTool.color,
//                     "&:hover": {
//                       borderColor: activeTool.color,
//                       backgroundColor: alpha(activeTool.color, 0.05),
//                     },
//                   }}
//                 >
//                   Save
//                 </Button>
//               </Tooltip>

//               <Tooltip title="Download as text file">
//                 <Button
//                   variant="contained"
//                   startIcon={<Download />}
//                   onClick={handleDownload}
//                   disabled={!generatedContent}
//                   fullWidth
//                   sx={{
//                     borderRadius: 1,
//                     background: activeTool.gradient,
//                     "&:hover": {
//                       background: activeTool.gradient,
//                       boxShadow: `0 8px 20px ${alpha(activeTool.color, 0.3)}`,
//                     },
//                   }}
//                 >
//                   Download
//                 </Button>
//               </Tooltip>
//             </Box>
//           </Paper>
//         </Grid>

//         {/* Saved Content Section */}
//         <Grid xs={12}>
//           <Paper
//             elevation={0}
//             sx={{
//               p: 3,
//               borderRadius: 3,
//               backgroundColor: "background.paper",
//               border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
//               boxShadow: `
//                 0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
//                 0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
//               `,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
//               <Typography variant="h6" fontWeight="bold">
//                 Saved Content
//               </Typography>
//               <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
//                 <TextField
//                   size="small"
//                   placeholder="Search saved content..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                   InputProps={{
//                     startAdornment: (
//                       <InputAdornment position="start">
//                         <Search fontSize="small" />
//                       </InputAdornment>
//                     ),
//                   }}
//                   sx={{ width: 250 }}
//                 />
//                 <Tabs
//                   value={selectedTab}
//                   onChange={(e, newValue) => setSelectedTab(newValue)}
//                   sx={{ minHeight: 36 }}
//                 >
//                   <Tab 
//                     icon={<Article />} 
//                     label="All" 
//                     sx={{ minHeight: 36, fontSize: '0.75rem' }} 
//                   />
//                   <Tab 
//                     icon={<Star />} 
//                     label="Starred" 
//                     sx={{ minHeight: 36, fontSize: '0.75rem' }} 
//                   />
//                 </Tabs>
//               </Box>
//             </Box>

//             {displayedContents.length > 0 ? (
//               <List>
//                 {displayedContents.map((item) => (
//                   <ListItem
//                     key={item.id}
//                     sx={{
//                       mb: 2,
//                       borderRadius: 2,
//                       border: `1px solid ${alpha(item.color, 0.1)}`,
//                       backgroundColor: alpha(item.color, 0.03),
//                       transition: "all 0.2s ease",
//                       "&:hover": {
//                         backgroundColor: alpha(item.color, 0.05),
//                         transform: "translateY(-2px)",
//                         boxShadow: `0 4px 12px ${alpha(item.color, 0.1)}`,
//                       },
//                     }}
//                   >
//                     <ListItemIcon>
//                       <Box
//                         sx={{
//                           p: 1,
//                           borderRadius: "10px",
//                           background: `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
//                           color: "white",
//                         }}
//                       >
//                         {item.icon}
//                       </Box>
//                     </ListItemIcon>
//                     <ListItemText
//                       primary={
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           <Typography variant="subtitle1" fontWeight="bold">
//                             {item.tool}
//                           </Typography>
//                           <Chip
//                             label={new Date(item.date).toLocaleDateString()}
//                             size="small"
//                             sx={{ height: 20, fontSize: '0.65rem' }}
//                           />
//                         </Box>
//                       }
//                       secondary={
//                         <Typography variant="body2" color="text.secondary">
//                           {item.content}
//                         </Typography>
//                       }
//                     />
//                     <Box sx={{ display: "flex", gap: 1 }}>
//                       <Tooltip title={starredItems.has(item.id) ? "Unstar" : "Star"}>
//                         <IconButton
//                           size="small"
//                           onClick={() => handleStarToggle(item.id)}
//                         >
//                           {starredItems.has(item.id) ? (
//                             <Star sx={{ color: "#fbbf24" }} />
//                           ) : (
//                             <StarBorder />
//                           )}
//                         </IconButton>
//                       </Tooltip>
//                       <Tooltip title="Delete">
//                         <IconButton
//                           size="small"
//                           onClick={() => handleDeleteSaved(item.id)}
//                         >
//                           <Delete fontSize="small" />
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </ListItem>
//                 ))}
//               </List>
//             ) : (
//               <Box
//                 sx={{
//                   textAlign: "center",
//                   py: 8,
//                   borderRadius: 2,
//                   backgroundColor: alpha(theme.palette.divider, 0.03),
//                   border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`,
//                 }}
//               >
//                 <Save sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.3), mb: 2 }} />
//                 <Typography variant="h6" color="text.secondary" gutterBottom>
//                   No saved content yet
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   Generate and save content to see it here
//                 </Typography>
//               </Box>
//             )}
//           </Paper>
//         </Grid>
//       </Grid>

//       {/* Fullscreen Dialog */}
//       <Dialog
//         fullScreen
//         open={fullscreenDialogOpen}
//         onClose={() => setFullscreenDialogOpen(false)}
//         TransitionComponent={Fade}
//       >
//         <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//           <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//             {activeTool.icon}
//             <Typography variant="h6">{activeTool.name} - Fullscreen View</Typography>
//           </Box>
//           <IconButton onClick={() => setFullscreenDialogOpen(false)}>
//             <FullscreenExit />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent>
//           <TextField
//             fullWidth
//             multiline
//             value={generatedContent}
//             onChange={(e) => setGeneratedContent(e.target.value)}
//             sx={{
//               "& .MuiOutlinedInput-root": {
//                 height: "100%",
//                 "& textarea": {
//                   height: "calc(100vh - 200px) !important",
//                 },
//               },
//             }}
//           />
//         </DialogContent>
//         <DialogActions sx={{ p: 2 }}>
//           <Button onClick={() => setFullscreenDialogOpen(false)}>Close</Button>
//           <Button onClick={handleCopy} startIcon={<ContentCopy />}>
//             Copy
//           </Button>
//           <Button onClick={handleDownload} variant="contained" startIcon={<Download />}>
//             Download
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {/* Save Dialog */}
//       <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
//         <DialogTitle>Save Generated Content</DialogTitle>
//         <DialogContent>
//           <Typography>Your content has been saved to your collection.</Typography>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setSaveDialogOpen(false)}>Close</Button>
//         </DialogActions>
//       </Dialog>

//       {/* Snackbar */}
//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={3000}
//         onClose={() => setSnackbar({ ...snackbar, open: false })}
//         anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//       >
//         <Alert
//           severity={snackbar.severity}
//           onClose={() => setSnackbar({ ...snackbar, open: false })}
//           sx={{ width: "100%" }}
//         >
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Container>
//   );
// };

// export default AIBusinessTools;